import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '@/store';
import { selectArmsSorted, selectAucResults, selectPkParameters } from '@/store/selectors';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Tooltip } from '@/components/ui/Tooltip';
import { LambdaZEditor } from './LambdaZEditor';
import {
  AucInterval as AI,
  TrapezoidalRule as TR,
  BlqRule as BR,
} from '@bioavail/shared-types';
import { formatNumber } from '@/utils/format';
import { PARTIAL_AUC_CONVENTIONS } from '@/config/reference';

/** Collapsible section 3 — AUC Computation (advanced, P1.5.8). */
export function AucComputationPanel() {
  const study = useStore((s) => s.studies.find((x) => x.id === s.activeStudyId));
  const setAucInterval = useStore((s) => s.setAucInterval);
  const setTrapezoidalRule = useStore((s) => s.setTrapezoidalRule);
  const arms = useStore(useShallow(selectArmsSorted));
  const aucResults = useStore(useShallow(selectAucResults));
  const pkParameters = useStore(useShallow(selectPkParameters));
  const [editorArmId, setEditorArmId] = useState<string | null>(null);

  if (!study) return null;

  const activeArm = arms[0];
  const directAuc = activeArm?.usesDirectAuc ?? false;

  // Absorption-window coverage (F-072) + regulatory convention cross-check
  // (F-075) — only relevant for partial-AUC (interval) mode.
  const showPartialHints =
    study.aucInterval === AI.Interval && !!study.partialAucWindow;
  const window = study.partialAucWindow;
  const pkTest = arms.find((a) => a.role === 'test')
    ? pkParameters.find((p) => p.armId === arms.find((a) => a.role === 'test')!.id)
    : undefined;
  const pkRef = arms.find((a) => a.role === 'reference')
    ? pkParameters.find((p) => p.armId === arms.find((a) => a.role === 'reference')!.id)
    : undefined;
  let inadequate = false;
  let hlfT: number | null = null;
  let hlfR: number | null = null;
  if (showPartialHints && window && pkTest && pkRef) {
    const coversT = window.end > pkTest.tmax;
    const coversR = window.end > pkRef.tmax;
    hlfT = pkTest.halfLife ? (window.end - pkTest.tmax) / pkTest.halfLife : null;
    hlfR = pkRef.halfLife ? (window.end - pkRef.tmax) / pkRef.halfLife : null;
    if (!coversT || !coversR) inadequate = true;
    if ((hlfT !== null && hlfT < 1) || (hlfR !== null && hlfR < 1)) inadequate = true;
  }
  const matchingConventions = showPartialHints && window
    ? PARTIAL_AUC_CONVENTIONS.filter(
        (c) => c.window.start === window.start && c.window.end === window.end,
      )
    : [];

  return (
    <CollapsibleSection sectionKey="calculator.aucPanel" title="AUC Computation" column="left">
      <div className="flex-col-gap-3">
        <Select
          label="AUC interval"
          value={study.aucInterval}
          onChange={(v) => setAucInterval(v as AI)}
          options={[
            { value: AI.ZeroToT, label: 'AUC(0-t)' },
            { value: AI.ZeroToInf, label: 'AUC(0-∞)' },
            { value: AI.Interval, label: 'Partial AUC (Phase 2)' },
            { value: AI.ZeroToTau, label: 'AUC(0-τ) Steady-state (Phase 2)', disabled: true },
          ]}
          hint="AUC(0-t) uses observed data only. AUC(0-∞) extrapolates the terminal tail via λ_z."
        />

        {showPartialHints && window ? (
          <div className="info-box">
            <h4>
              Absorption-window coverage
              <Tooltip label="Partial AUC definition" learnMore={{ label: 'See regulatory guidance', screen: 'reference' }}>
                A partial AUC integrates concentration over a regulated window
                (e.g. 0–2 h for immediate-release products). It must span Tmax
                and extend past the absorption phase to be informative for
                bioequivalence.
              </Tooltip>
            </h4>
            {pkTest && pkRef ? (
              <p>
                Window <span className="mono">{window.start}–{window.end}</span> h:
                covers Tmax (test={pkTest.tmax}, ref={pkRef.tmax}); ends{' '}
                <span className="mono">
                  {hlfT !== null ? hlfT.toFixed(2) : '—'} /{' '}
                  {hlfR !== null ? hlfR.toFixed(2) : '—'}
                </span>{' '}
                t½ post-peak.
                {inadequate ? <span className="inline-badge">inadequate</span> : null}
              </p>
            ) : (
              <p className="field-hint">
                Set a partial-AUC window and compute to see absorption-window coverage.
              </p>
            )}
            {matchingConventions.length > 0 ? (
              matchingConventions.map((c) => (
                <p key={`${c.authority}-${c.formulation}`} className="field-hint">
                  Matches {c.authority} convention for {c.formulation}.
                </p>
              ))
            ) : (
              <p className="field-hint">
                No FDA/EMA convention matches this window — document the rationale in the study report.
              </p>
            )}
          </div>
        ) : null}

        {directAuc ? (
          <p className="field-hint">Direct AUC mode — trapezoidal rule and λ_z not applicable.</p>
        ) : (
          <>
            <Select
              label="Trapezoidal rule"
              value={activeArm?.trapezoidalRule ?? TR.LinearUpLogDown}
              onChange={(v) => activeArm && setTrapezoidalRule(activeArm.id, v as TR)}
              options={[
                { value: TR.LinearUpLogDown, label: 'Linear-up / log-down (default)' },
                { value: TR.LinearTrapezoidal, label: 'Linear trapezoidal' },
                { value: TR.LinearLog, label: 'Linear / log' },
              ]}
              hint="Linear-up / log-down is the regulatory standard for most PK submissions."
            />

            <Select
              label="BLQ rule"
              value={activeArm?.blqRule ?? BR.M1}
              onChange={(v) => activeArm && useStore.getState().updateArm(activeArm.id, { blqRule: v as BR })}
              options={[
                { value: BR.M1, label: 'M1 — treat BLQ as 0' },
                { value: BR.M2, label: 'M2 — treat BLQ as LLOQ/2 (Phase 2)', disabled: true },
              ]}
              hint="M1 sets below-limit-of-quantification samples to 0 before integration."
            />

            {study.aucInterval === AI.ZeroToInf ? (
              <div>
                <span className="field-label">
                  λ_z fit summary
                  <Tooltip label="Terminal elimination rate constant" learnMore={{ label: 'See worked examples', screen: 'reference' }}>
                    λ_z is estimated by log-linear regression on the terminal phase. A fit
                    with R² &lt; 0.85 or extrapolation &gt; 20% may be unreliable.
                  </Tooltip>
                </span>
                {arms.map((arm) => {
                  const auc = aucResults.find((a) => a.armId === arm.id);
                  const r2 = auc?.lambdaZR2 ?? 0;
                  return (
                    <div key={arm.id} className="stat-line">
                      <span className="stat-line-label">{arm.label}</span>
                      <span className="stat-line-value">
                        λ_z={formatNumber(auc?.lambdaZ)} · R²={formatNumber(r2)}
                        {r2 > 0 && r2 < 0.85 ? <span className="inline-badge">low R²</span> : null}
                        {' · '}extrap={formatNumber(auc?.extrapolationPercent)}%
                      </span>
                      <span className="stat-line-action">
                        <Button size="sm" variant="ghost" onClick={() => setEditorArmId(arm.id)}>
                          Edit
                        </Button>
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </>
        )}
      </div>

      {editorArmId ? (
        <Modal open title="Edit terminal points" onClose={() => setEditorArmId(null)}>
          <LambdaZEditor armId={editorArmId} />
        </Modal>
      ) : null}
    </CollapsibleSection>
  );
}
